import { FILTER_RESULTS } from '@/components/ui/filter';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { type SessionStatus } from '@heidi/schema/ml-scribe';
import Fuse from 'fuse.js';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  consolidatedSelectedDateRangeFilterAtom,
  consolidatedSelectedPatientProfilesFilterAtom,
  consolidatedSelectedStatusesFilterAtom,
  consolidatedSessionsSortByAtom,
  consolidatedSessionsSortOrderAtom,
} from '../atoms';

export const useConsolidatedSessionFilters = () => {
  const [selectedDateRangeFilter, setSelectedDateRangeFilter] = useAtom(
    consolidatedSelectedDateRangeFilterAtom
  );
  const [selectedStatusesFilter, setSelectedStatusesFilter] = useAtom(
    consolidatedSelectedStatusesFilterAtom
  );
  const [selectedPatientProfilesFilter, setSelectedPatientProfilesFilter] = useAtom(
    consolidatedSelectedPatientProfilesFilterAtom
  );
  const [sortBy, setSortBy] = useAtom(consolidatedSessionsSortByAtom);
  const [sortOrder, setSortOrder] = useAtom(consolidatedSessionsSortOrderAtom);
  const { data: unsortedPatientProfiles } = useQueryPatientProfiles();
  const [isPatientFilterOpen, setIsPatientFilterOpen] = useState(false);
  const [patientProfiles, setPatientProfiles] = useState(unsortedPatientProfiles);
  const hasSortedOnOpen = useRef(false);
  const lastUnsortedProfilesRef = useRef(unsortedPatientProfiles);
  const searchMatchesCacheRef = useRef(new Map<string, Set<string>>());
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    const hasSourceChangedWhileOpen =
      lastUnsortedProfilesRef.current !== unsortedPatientProfiles;

    if (isPatientFilterOpen && (!hasSortedOnOpen.current || hasSourceChangedWhileOpen)) {
      const sortedProfiles = [...(unsortedPatientProfiles ?? [])].sort((a, b) => {
        const aIsSelected = selectedPatientProfilesFilter.includes(a.id ?? '');
        const bIsSelected = selectedPatientProfilesFilter.includes(b.id ?? '');
        if (aIsSelected && !bIsSelected) return -1;
        if (!aIsSelected && bIsSelected) return 1;
        return 0;
      });
      setPatientProfiles(sortedProfiles);
      hasSortedOnOpen.current = true;
    } else if (!isPatientFilterOpen) {
      hasSortedOnOpen.current = false;
      setPatientProfiles(unsortedPatientProfiles);
    }

    lastUnsortedProfilesRef.current = unsortedPatientProfiles;
  }, [isPatientFilterOpen, unsortedPatientProfiles, selectedPatientProfilesFilter]);

  const fuse = useMemo(
    () =>
      new Fuse(patientProfiles ?? [], {
        keys: ['demographic_string'],
        threshold: 0.4,
      }),
    [patientProfiles]
  );

  useEffect(() => {
    searchMatchesCacheRef.current.clear();
  }, [fuse]);

  const getSearchMatchIds = useCallback(
    (search: string) => {
      const cachedMatchIds = searchMatchesCacheRef.current.get(search);
      if (cachedMatchIds) {
        return cachedMatchIds;
      }

      const matchIds = new Set(
        fuse
          .search(search)
          .map((result) => result.item.id)
          .filter((id): id is string => Boolean(id))
      );
      searchMatchesCacheRef.current.set(search, matchIds);
      return matchIds;
    },
    [fuse]
  );

  const handleFilter = useCallback(
    (value: string, search: string) => {
      const matchIds = getSearchMatchIds(search);
      if (matchIds.has(value)) {
        return FILTER_RESULTS.MATCH;
      }
      return FILTER_RESULTS.NO_MATCH;
    },
    [getSearchMatchIds]
  );

  const handleSelectPatientProfile = useCallback(
    (profileId: string) => {
      setSelectedPatientProfilesFilter((prev) =>
        prev.includes(profileId)
          ? prev.filter((id) => id !== profileId)
          : [...prev, profileId]
      );
    },
    [setSelectedPatientProfilesFilter]
  );

  const handleStatusChange = useCallback(
    (status: SessionStatus) => {
      setSelectedStatusesFilter((prev) => {
        if (prev.includes(status)) {
          return prev.filter((s) => s !== status);
        }
        return [...prev, status];
      });
    },
    [setSelectedStatusesFilter]
  );

  const isFilterApplied = useMemo(() => {
    return (
      Boolean(selectedDateRangeFilter.from && selectedDateRangeFilter.to) ||
      selectedStatusesFilter.length > 0 ||
      selectedPatientProfilesFilter.length > 0 ||
      sortBy !== 'createdAt' ||
      sortOrder !== 'desc'
    );
  }, [
    selectedDateRangeFilter.from,
    selectedDateRangeFilter.to,
    selectedStatusesFilter,
    selectedPatientProfilesFilter,
    sortBy,
    sortOrder,
  ]);

  const resetFilters = useCallback(() => {
    setSelectedDateRangeFilter({ from: undefined, to: undefined });
    setSelectedStatusesFilter([]);
    setSelectedPatientProfilesFilter([]);
    setSortBy('createdAt');
    setSortOrder('desc');
    setSearchValue('');
  }, [
    setSelectedDateRangeFilter,
    setSelectedStatusesFilter,
    setSelectedPatientProfilesFilter,
    setSortBy,
    setSortOrder,
  ]);

  return {
    selectedDateRangeFilter,
    setSelectedDateRangeFilter,
    selectedStatusesFilter,
    handleStatusChange,
    selectedPatientProfilesFilter,
    patientProfiles,
    searchValue,
    setSearchValue,
    handleFilter,
    handleSelectPatientProfile,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    setIsPatientFilterOpen,
    isFilterApplied,
    resetFilters,
  };
};
